/**
 * App.js — Root router for M16S.
 *
 * All routes are wrapped in Layout (Navbar + optional Footer) and sorted into
 * three groups:
 *   1. Public routes      — accessible to anyone, no auth required.
 *   2. Auth routes        — login/signup; redirect authenticated users away.
 *   3. Protected routes   — require a valid JWT and an approved account.
 *   4. Admin-only routes  — require the authenticated user to have role=admin.
 *
 * Route guards are enforced by <ProtectedRoute> and <PublicRoute>. See
 * src/components/ProtectedRoute.jsx for guard logic details.
 */
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { TooltipProvider } from './components/ui/tooltip';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AdminDashboard from './pages/AdminDashboard';
import { Layout } from './components/layout/Layout';
import AnalysisList from './pages/AnalysisList';
import BatchVisualizations from './pages/BatchVisualizations';
import Epi2meResults from './pages/Epi2meResults';
import AnalysisLogs from './pages/AnalysisLogs';
import BatchProjectUpload from './pages/BatchProjectUpload';
import BatchAnalysisMonitor from './pages/BatchAnalysisMonitor';
import CoiProjectUpload from './pages/CoiProjectUpload';
import CoiResults from './pages/CoiResults';
import MetadataViewer from './pages/MetadataViewer';
import MetadataDatabase from './pages/MetadataDatabase';
import NotFound from './pages/NotFound';
import Unauthorized from './pages/Unauthorized';
import { ProtectedRoute, PublicRoute } from './components/ProtectedRoute';
import MetadataEditor from './pages/MetadataEditor';


function App() {
  return (
    <TooltipProvider>
      <Router>
        <Routes>
          {/* ── Public routes ──────────────────────────────────────────────────
            No authentication required. Landing redirects logged-in users to
            /dashboard so they don't land on the marketing page again.         */}
          <Route path="/" element={<Layout><PublicRoute redirectTo="/dashboard"><Landing /></PublicRoute></Layout>} />
          <Route path="/unauthorized" element={<Layout><Unauthorized /></Layout>} />

          {/* ── Auth routes ────────────────────────────────────────────────────
            Login and Signup. <PublicRoute> without an explicit redirectTo uses
            role-based defaults: admins go to /admin, everyone else /dashboard. */}
          <Route path="/login" element={
            <Layout>
              <PublicRoute>
                <Login />
              </PublicRoute>
            </Layout>
          } />
          <Route path="/signup" element={
            <Layout>
              <PublicRoute>
                <Signup />
              </PublicRoute>
            </Layout>
          } />

          {/* ── Protected routes ───────────────────────────────────────────────
            Require a valid JWT token AND an approved account status.
            Unauthenticated users are redirected to /login.
            Authenticated but unapproved users are redirected to /unauthorized. */}

          {/* Legacy URL redirect — keep old links working. */}
          <Route path="/project/new" element={<Navigate to="/project/batch/new" replace />} />

          {/* Dashboard: lists all analyses for the current user. */}
          <Route path="/dashboard" element={
            <Layout showFooter={false}>
              <ProtectedRoute>
                <AnalysisList />
              </ProtectedRoute>
            </Layout>
          } />

          {/* Batch visualizations: abundance charts and heatmaps for a project. */}
          <Route path="/analysis/:id/batch" element={
            <Layout showFooter={false}>
              <ProtectedRoute>
                <BatchVisualizations />
              </ProtectedRoute>
            </Layout>
          } />

          {/* Epi2me results: raw Nextflow/Kraken2 output viewer for a project. */}
          <Route path="/analysis/:id/results" element={
            <Layout showFooter={false}>
              <ProtectedRoute>
                <Epi2meResults />
              </ProtectedRoute>
            </Layout>
          } />

          {/* Legacy redirect — /analysis/get-all is no longer a page. */}
          <Route path="/analysis/get-all" element={<Navigate to="/dashboard" replace />} />

          {/* Analysis logs: live WebSocket log stream or historical log viewer. */}
          <Route path="/analysis/:id/logs" element={
            <Layout showFooter={false}>
              <ProtectedRoute>
                <AnalysisLogs />
              </ProtectedRoute>
            </Layout>
          } />

          {/* Batch project upload: step-through form to create a project and upload batch zips. */}
          <Route path="/project/batch/new" element={
            <Layout showFooter={false}>
              <ProtectedRoute>
                <BatchProjectUpload />
              </ProtectedRoute>
            </Layout>
          } />

          {/* COI / Eukaryota project upload: QIIME 2 single-batch pipeline. */}
          <Route path="/project/coi/new" element={
            <Layout showFooter={false}>
              <ProtectedRoute>
                <CoiProjectUpload />
              </ProtectedRoute>
            </Layout>
          } />

          {/* COI / Eukaryota analysis results page. */}
          <Route path="/analysis/:id/coi-results" element={
            <Layout showFooter={false}>
              <ProtectedRoute>
                <CoiResults />
              </ProtectedRoute>
            </Layout>
          } />

          {/* Batch analysis monitor: per-batch status polling for a multi-batch project. */}
          <Route path="/analysis/:parentId/manage" element={
            <Layout showFooter={false}>
              <ProtectedRoute>
                <BatchAnalysisMonitor />
              </ProtectedRoute>
            </Layout>
          } />

          {/* Metadata database: central hub for all metadata across all projects. */}
          <Route path="/metadata" element={
            <Layout showFooter={false}>
              <ProtectedRoute>
                <MetadataDatabase />
              </ProtectedRoute>
            </Layout>
          } />

          {/* Metadata viewer: list and export metadata sheets for a project. */}
          <Route path="/project/:projectId/metadata" element={
            <Layout showFooter={false}>
              <ProtectedRoute>
                <MetadataViewer />
              </ProtectedRoute>
            </Layout>
          } />

          {/* Metadata editor: full-page create/edit — rendered standalone, not nested under MetadataViewer */}
          <Route path="/project/:projectId/metadata/new" element={
            <Layout showFooter={false}>
              <ProtectedRoute>
                <MetadataEditor />
              </ProtectedRoute>
            </Layout>
          } />
          <Route path="/project/:projectId/metadata/:sheetId/edit" element={
            <Layout showFooter={false}>
              <ProtectedRoute>
                <MetadataEditor />
              </ProtectedRoute>
            </Layout>
          } />

          {/* ── Admin-only routes ──────────────────────────────────────────────
            requireAdmin checks role === 'admin'. Non-admins are sent to
            /unauthorized regardless of their approval status.                 */}
          <Route path="/admin" element={
            <Layout showFooter={false}>
              <ProtectedRoute requireAdmin>
                <AdminDashboard />
              </ProtectedRoute>
            </Layout>
          } />

          {/* ── 404 catch-all ──────────────────────────────────────────────── */}
          <Route path="*" element={<Layout><NotFound /></Layout>} />
        </Routes>
      </Router>
    </TooltipProvider>
  );
}
export default App;
